import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { isAuthenticated, logout, me, whoami } from './api/auth';
import { onUnauthorized } from './api/client';
import { realtimeClient } from './api/realtime';
import { ApiError } from './api/client';
import { canAudition, canDeleteSongs, canDownload, canIngest, canInviteStaff, canManageAccounts, canManageLicences, canManageRooms, canManageStaff, canMoveSessionRoom, canOperate, canPayForPlan, canReviewPayments, canRotateJoinCode, canTagGuideVocal } from './api/roles';
import { useConnectionHealth } from './app/useConnectionHealth';
import { useT } from './i18n/i18n.tsx';
import type { MessageKey } from './i18n/messages.en.ts';
import { addSinger as addSingerToRotation, createQueueEntry as createRotationQueueEntry, getRotationState, patchQueueEntry as patchRotationQueueEntry } from './api/rotation';
import { listSingers } from './api/singers';
import type { LicenceSnapshot, Singer, Song, StrapiUser, UserRole } from './api/types';
import { guestPath } from './app/route';
import { useRoute } from './app/useRoute';
import { LoginDialog } from './features/auth/LoginDialog';
import { InviteRedeemScreen } from './features/auth/InviteRedeemScreen';
import { SignupScreen } from './features/auth/SignupScreen';
import { SignupConfirmScreen } from './features/auth/SignupConfirmScreen';
import { ProfileDialog } from './features/account/ProfileDialog';
import { RequestPage } from './features/guest/RequestPage';
import { ConnectionIndicator } from './features/ops/ConnectionIndicator';
import { PlaybackStateProvider, usePlaybackStateContext } from './features/ops/usePlaybackState';
import { TransportBridgeProvider } from './features/ops/useTransportBridge';
import { MiniTransportBar } from './features/ops/MiniTransportBar';
import { useSessionInfo } from './features/ops/useSessionInfo';
import { cuedSongPatch } from './features/ops/playbackMath';
import { pendingAutoplaySongIdRef } from './features/player/pendingAutoplayRef';
import { applyDocumentTheme, applyDocumentPalette } from './features/ops/theme';
import { effectiveHotkeyBindings, isTypingTarget } from './features/ops/hotkeys.ts';
import { requestSearchFocus, shouldOpenConsoleSearch } from './features/ops/consoleSearchFocus.ts';
import { useOpsSettings } from './features/ops/useOpsSettings';
// MVP-500: the mode's state lives in `useStorefrontMode`, and everything it means — including why
// it is toggled explicitly rather than derived from anything the browser does on its own — is
// written in `storefrontMode.ts` and `StorefrontModeBar.tsx`, not here.
import { useStorefrontMode } from './features/ops/useStorefrontMode';
import { StorefrontModeBar } from './features/ops/StorefrontModeBar';
// MVP-513: the venue's licence countdown, a sibling strip. Statically imported for the same reason
// the storefront strip above is — it is a row of the console shell's own chrome, not a screen the
// rail switches to, and the six screens are what `guestBundle.test.ts` keeps out of the entry chunk.
import { LicenceExpiryBar } from './features/ops/LicenceExpiryBar';
import { AppHeader, Button, AlertToast, SidebarRail, Skeleton, StatusBar, ToastProvider, type SidebarRailItem } from './ui';
import './App.css';
import { apiErrorMessage } from './api/errorMessage.ts';

// Console and Stage are lazy-loaded so the guest bundle (`/j/<code>`, AC-REQ-2's <=150KB gz) never
// pulls jassub/@soundtouchjs/usePlaybackEngine/the lyrics engine — both branches below import them
// transitively (StageRoot -> LyricStage -> jassub; OperatorConsole -> usePlaybackEngine ->
// audioGraph -> @soundtouchjs) and neither is reachable from the guest or stage routes' own code
// paths, so nothing forces them into a chunk a phone on cellular data has to download.
const StageRoot = lazy(() => import('./features/ops/StageRoot').then((m) => ({ default: m.StageRoot })));
const OperatorConsole = lazy(() => import('./features/ops/OperatorConsole').then((m) => ({ default: m.OperatorConsole })));

// MVP-365: everything behind the App shell is lazy for the same reason Console and Stage are. The
// guest route (`/j/<code>`) returns `RequestPage` above any auth branch and can reach NONE of these
// — yet every one of them was sitting in the same `index` chunk a phone on cellular data has to
// download before it can search for a song. That chunk measured 143 KB gz against AC-REQ-2's
// 150 KB budget, i.e. the budget was being met by 7 KB while carrying four admin screens, the
// 1200-line Library and an XLSX writer that only an operator's export click can ever run.
//
// `LibraryScreen` is lazy even though it is mounted for the whole session (hidden with
// `display:none` rather than unmounted, see its wrapper below): "mounted once, forever" and
// "fetched as part of the first paint" are different questions, and only the second one costs a
// guest anything. The Suspense fallback it can show is a one-time, post-login pane.
//
// The XLSX writer is deliberately NOT split further. It is ~330 lines that ride along inside
// LibraryScreen's chunk, and an operator who has already downloaded the Library is not the person
// this task is about.
const LibraryScreen = lazy(() => import('./features/library/LibraryScreen').then((m) => ({ default: m.LibraryScreen })));
const SettingsScreen = lazy(() => import('./features/ops/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
// MVP-508: lazy like every other post-login screen, and for the reason at the top of this block —
// the guest bundle is capped at 150 KB (AC-REQ-2) and a room-management screen no guest can reach
// must not be inside it.
const RoomsScreen = lazy(() => import('./features/ops/RoomsScreen').then((m) => ({ default: m.RoomsScreen })));
const LicencesScreen = lazy(() => import('./features/admin/LicencesScreen').then((m) => ({ default: m.LicencesScreen })));
const AdminIngestScreen = lazy(() => import('./features/admin/AdminIngestScreen').then((m) => ({ default: m.AdminIngestScreen })));
const AdminUsersScreen = lazy(() => import('./features/admin/AdminUsersScreen').then((m) => ({ default: m.AdminUsersScreen })));
const AdminActivityScreen = lazy(() => import('./features/admin/AdminActivityScreen').then((m) => ({ default: m.AdminActivityScreen })));
const AdminWithheldScreen = lazy(() => import('./features/admin/AdminWithheldScreen').then((m) => ({ default: m.AdminWithheldScreen })));
// MVP-278: the shop's plans page and the admin's slip queue — lazy like every post-login screen.
const PlansScreen = lazy(() => import('./features/plans/PlansScreen').then((m) => ({ default: m.PlansScreen })));
const AdminPaymentsScreen = lazy(() => import('./features/admin/AdminPaymentsScreen').then((m) => ({ default: m.AdminPaymentsScreen })));

type ConsoleView = 'library' | 'cockpit' | 'settings' | 'ingest' | 'users' | 'activity' | 'withheld' | 'rooms' | 'licences' | 'plans' | 'payments';

// R8-P1-4 fix (round 8, live-Chrome critic): "restore the last view" on reload — this used to be
// in-memory only by explicit ponytail design ("resets to Library on reload"), which the round-8
// critic flagged as part of the same reload-loses-playback bug: a KJ mid-song on the Cockpit tab
// who refreshes now lands back on Library, and (before the fix below) with no `<video>` even
// mounted. `sessionStorage` (not `localStorage`) so a genuinely new tab/window still starts on
// Library rather than an unrelated later session inheriting whichever tab happened to be open.
const VIEW_STORAGE_KEY = 'mvcp.consoleView';
function loadStoredView(): ConsoleView {
  try {
    const stored = sessionStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'cockpit' || stored === 'settings' || stored === 'ingest' || stored === 'users' || stored === 'activity' || stored === 'withheld' || stored === 'rooms'
      ? stored
      : 'library';
  } catch {
    return 'library'; // storage disabled/unavailable — the old, always-safe default
  }
}

// MVP-11 #8: the rail's third icon — SidebarRail.tsx's own doc comment names a third
// "Library / Cockpit / Settings" and SettingsScreen.tsx now exists to open there.
//
// The Library is the only entry every signed-in role can use, so it is the only one outside a
// gate. Everything below is added back per role.
/**
 * MVP-272 Phase 3c-1: these are module constants, evaluated before any component mounts, so they
 * cannot call `t()` — they carry the message KEY and `navItems` below turns each one into a
 * `SidebarRailItem` at render. `SidebarRail` itself stays dictionary-free (`ui/` primitives take
 * finished text — see `SearchField.tsx`'s note on why), so the translation happens exactly at the
 * boundary between this app's copy and that primitive.
 */
type NavSpec = { id: string; icon: SidebarRailItem['icon']; labelKey: MessageKey };

const LIBRARY_NAV_ITEM: NavSpec = { id: 'library', icon: 'grid', labelKey: 'nav.library' };

// The two session-running screens. A `downloader` has read-only catalog access and nothing else
// (`DOWNLOADER_ACTIONS` in strapi-template/src/bootstrap/roles.js), so both of these were doors to
// a wall for them — every request either screen makes came back 403.
const OPERATOR_NAV_ITEMS: NavSpec[] = [
  { id: 'cockpit', icon: 'monitor', labelKey: 'nav.cockpit' },
  // MVP-463: a gear, not `sliders` — the rail sat one icon away from the library's filter control
  // and used the same three-line glyph for it.
  { id: 'settings', icon: 'gear', labelKey: 'nav.settings' },
];

// MVP-508: the `shop` role's own rail entry — the venue's rooms, under its licence cap. `rows` and
// not `monitor`: a shop passes `canOperate`, so it already has the Cockpit's monitor two icons up,
// and MVP-463's finding was that two rail entries sharing a glyph is how the wrong one gets pressed.
// This screen is a list of the shop's rooms, which is what `rows` says and what no other rail entry
// claims.
const ROOMS_NAV_ITEM: NavSpec = { id: 'rooms', icon: 'rows', labelKey: 'nav.rooms' };

// MVP-509: the vendor's own rail entry — the customers who hold a package and the branches each
// runs. `theater` and not `rows`: an admin passes `canManageRooms` too, so the Rooms entry is
// already on this rail with the two-bar glyph, and MVP-463's finding was that two entries reading
// as the same shape is how the wrong one gets pressed. `theater` is a frame inside a frame — the
// only nested-rectangle glyph in the set, and the one that reads as a branch rather than a list.
const LICENCES_NAV_ITEM: NavSpec = { id: 'licences', icon: 'theater', labelKey: 'nav.licences' };

// MVP-225: the admin-only rail entry. Appended rather than folded into NAV_ITEMS above so a
// non-admin's rail is byte-for-byte what it was before this task.
// MVP-466: the arrow points INTO the tray. `upload` pointed out of it, which reads as sending
// something away — the opposite of what "นำเข้า" says one tooltip later. The glyph describes the
// direction, so it is shared with the catalogue's download control without either lying.
const INGEST_NAV_ITEM: NavSpec = { id: 'ingest', icon: 'download', labelKey: 'nav.ingest' };

// MVP-226: the other admin-only rail entry — accounts and the invite links that create them.
const USERS_NAV_ITEM: NavSpec = { id: 'users', icon: 'user', labelKey: 'nav.accounts' };

// MVP-227: the audit log. Its own entry rather than a section under Accounts, because it is a list
// you come to read rather than one you edit — under two editable lists it sat below a scroll nobody
// made.
const ACTIVITY_NAV_ITEM: NavSpec = { id: 'activity', icon: 'activity', labelKey: 'nav.activity' };

// MVP-358: the same admin-only trip as the three above — the trash for the KJ app, and (until it
// existed) the missing screen the Undo toast on a delete was the only way around. `inbox` reads as
// "a place things are held" rather than "gone", which is exactly the semantic split between this
// screen and a hard delete (which the app never does — see `deleteSong`'s own doc comment).
const WITHHELD_NAV_ITEM: NavSpec = { id: 'withheld', icon: 'inbox', labelKey: 'nav.withheld' };

// MVP-278: the shop's plans-and-renewal page (`clock`: these are time-based plans), and the admin's
// slip queue (`check`: the screen exists to approve). Neither glyph is on the rail already.
const PLANS_NAV_ITEM: NavSpec = { id: 'plans', icon: 'clock', labelKey: 'nav.plans' };
const PAYMENTS_NAV_ITEM: NavSpec = { id: 'payments', icon: 'check', labelKey: 'nav.payments' };

/** The views behind the `admin` gate. A stored view from an admin session falls back to Library for
 * anyone else, rather than rendering a screen whose every request would 403. One set, so adding a
 * fifth admin screen cannot leave half the check behind. */
const ADMIN_VIEWS = new Set<ConsoleView>(['ingest', 'activity', 'withheld']);

/** MVP-464: `users` is no longer one of them. A `shop` reaches that screen too — to issue a link to
 * its own staff, and for nothing else: the screen's account list is gated separately, by
 * `canManageAccounts`. Its own set rather than a second membership in ADMIN_VIEWS, because the two
 * questions ("may this role open the screen" and "may it see the roster on it") now have different
 * answers and a stored `mvcp.consoleView` is checked against the first. */
const STAFF_INVITE_VIEWS = new Set<ConsoleView>(['users']);

/** MVP-508: the same idea again for the Rooms screen — `shop` only, gated by `canManageRooms`,
 * whose doc comment names `ROOM_WRITE_ACTIONS` as the real boundary. Its own set for the reason
 * STAFF_INVITE_VIEWS is: these are three different backend grants and a stored `mvcp.consoleView`
 * has to be checked against the one that actually answers for the screen it names. */
const ROOM_ADMIN_VIEWS = new Set<ConsoleView>(['rooms']);

/** MVP-509: and once more for the Licences screen — `admin` alone, gated by `canManageLicences`,
 * whose doc comment names LICENCE_ADMIN_ACTIONS and VENUE_ADMIN_ACTIONS as the real boundary. Its
 * own set rather than a second membership in ADMIN_VIEWS for the reason the two above have one:
 * these are different backend grants, and a stored `mvcp.consoleView` has to be checked against the
 * grant that actually answers for the screen it names. */
const LICENCE_ADMIN_VIEWS = new Set<ConsoleView>(['licences']);

/** MVP-278: one set per grant again — `plans` is `PAYMENT_SHOP_ACTIONS` (`canPayForPlan`, shop),
 * `payments` is `PAYMENT_ADMIN_ACTIONS` (`canReviewPayments`, admin). */
const PLAN_SHOP_VIEWS = new Set<ConsoleView>(['plans']);
const PAYMENT_ADMIN_VIEWS = new Set<ConsoleView>(['payments']);

/** The same idea one gate down: views a `downloader` has no permissions for. Kept beside
 * ADMIN_VIEWS rather than derived from OPERATOR_NAV_ITEMS so that the fallback and the rail are
 * read as two deliberate lists — a view can exist without a rail entry, and the fallback is what
 * actually protects a stored `mvcp.consoleView` from another account's session. */
const OPERATOR_VIEWS = new Set<ConsoleView>(['cockpit', 'settings']);

/** MVP-500 Commit B: the views storefront mode takes away — every screen that changes
 * configuration or the catalogue. Library and Cockpit are what is left, which is the whole point:
 * the room can still find a song and the KJ can still run the night.
 *
 * It gates BOTH the rail (a screen with no way to reach it) and `effectiveView` (a screen already
 * open when the mode engages falls back to Library), for the same reason the role gates below do:
 * a stored `mvcp.consoleView` and a mid-session state change are two different ways into the same
 * screen and one list has to answer both. This is not a security boundary — the backend refuses
 * these requests on role regardless, and the mode is per-machine and self-declared. */
// MVP-508 adds `rooms`: opening or closing a room is configuration of the shop itself, which is
// precisely what this mode takes away while the machine is out on the floor.
// MVP-509 adds `licences` on the same reasoning, one level up: this is where a customer's package
// and their branches are changed, which is not something to leave reachable from a machine standing
// unattended on a shop floor.
const STOREFRONT_HIDDEN_VIEWS = new Set<ConsoleView>(['settings', 'ingest', 'users', 'activity', 'withheld', 'rooms', 'licences', 'plans', 'payments']);

/**
 * The authenticated KJ app: one `AppHeader`/`StatusBar`, a `SidebarRail` (its own doc comment:
 * "the first real router-equivalent App.tsx has needed") switching between the Library screen and
 * the Operator cockpit (DESIGN.md §9.2/§10) — the two have no state in common, so only one is
 * mounted at a time rather than both staying alive off-screen.
 *
 * Owns the glue `LibraryScreen`'s `onQueue`/`onPlayNow`/`singers` props need: resolving a singer
 * (`api/singers.ts`), computing a rank (`features/rotation/queueRank.ts`) and writing the queue
 * row (`api/queue.ts`) for `onQueue`; a direct `usePlaybackStateContext().patch()` for `onPlayNow`,
 * which by design (LibraryScreen.tsx's own prop contract — a plain `(song) => void`, no singer, no
 * queue row) bypasses the rotation entirely. `OperatorConsole` owns the only decoder in the app and
 * is not necessarily mounted when a "Play now" happens from the Library tab; writing through the
 * same shared `PlaybackStateProvider` instance `OperatorConsole`/`usePlaybackEngine` also read from
 * (rather than reaching into `OperatorConsole`, which exposes no imperative handle for this) lands
 * the new song in server-side playback state immediately, and switches the rail to Cockpit so the
 * KJ sees it — either picked up live by that already-mounted shared instance (SSE/
 * BroadcastChannel), or loaded fresh the moment `OperatorConsole` itself mounts.
 */
// R11-P1-2 fix (round 11, live-Chrome critic): `usePlaybackStateContext()` (used by this shell,
// `OperatorConsole.tsx`, and `usePlaybackEngine.ts`) throws unless it is rendered under a
// `PlaybackStateProvider` — see that hook's own doc comment for the three-redundant-instances bug
// this consolidation closes. `ConsoleShell` is that provider's one mount point for the whole
// console route; everything that needs playback state lives inside it.
// `ToastProvider` is mounted HERE, not higher up in `App()` — this is the KJ console's own subtree,
// reached only past the `route.kind === 'stage'`/`'guest'` early returns below. DESIGN.md: nothing
// from the App renders on the Stage, and the guest page has no operator-facing transient status.
function ConsoleShell(props: { user: StrapiUser | null; role: UserRole | null; licence: LicenceSnapshot | null; onSignOut: () => void }) {
  const t = useT();
  return (
    <ToastProvider dismissLabel={t('common.dismiss')}>
      <PlaybackStateProvider>
        <TransportBridgeProvider>
          <ConsoleShellInner {...props} />
        </TransportBridgeProvider>
      </PlaybackStateProvider>
    </ToastProvider>
  );
}

function ConsoleShellInner({
  user,
  role,
  licence,
  onSignOut,
}: {
  user: StrapiUser | null;
  role: UserRole | null;
  licence: LicenceSnapshot | null;
  onSignOut: () => void;
}) {
  const [view, setViewState] = useState<ConsoleView>(loadStoredView);
  const setView = useCallback((next: ConsoleView) => {
    setViewState(next);
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* storage disabled — the view still switches for this render, just doesn't survive a reload */
    }
  }, []);
  const [singers, setSingers] = useState<Singer[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);

  // MVP-335: switching the rail used to leave focus wherever it was and announce nothing — measured
  // live: a keyboard user resumed tabbing mid-rail, and a screen-reader user had no signal the
  // content behind them had changed at all. `viewRegionRef` wraps every one of the six screens
  // (the OperatorConsole's always-mounted-but-hidden wrapper included, so this covers all six
  // without needing a per-screen hook — see the file-ownership note below), and gets focused
  // whenever `effectiveView` changes; `viewAnnouncement` is the matching text for the visually-hidden
  // `aria-live="polite"` region beside the skip link. `<h1 className="visually-hidden">` below is
  // NOT the focus target — a11yLandmarks.test.ts pins its exact markup, and this task's own house
  // rules say not to touch LibraryScreen.tsx/AdminUsersScreen.tsx/SettingsScreen.tsx to give any one
  // of them its own focus hook, so the mechanism lives here, generically, above all six.
  //
  // `hasMountedViewRef` skips the very first run: the initial view on mount is not a "change" a
  // keyboard/screen-reader user just triggered, and stealing focus off whatever the browser already
  // put it on (or announcing a view nobody switched to) would be its own bug.
  const viewRegionRef = useRef<HTMLDivElement>(null);
  const [viewAnnouncement, setViewAnnouncement] = useState('');
  const hasMountedViewRef = useRef(false);
  // MVP-490: set for the ONE view change `/` itself caused, and consumed in that same commit by the
  // effect below. It cannot be replaced by reading `consoleSearchFocus.ts`'s singleton here: React
  // runs child effects before parent effects, so on an already-mounted LibraryScreen the child has
  // already consumed the singleton by the time this parent effect runs — and the focus move would
  // land right back on the view region, which is exactly the bug.
  const suppressViewFocusRef = useRef(false);

  // MVP-500: per-machine, not per-session — `storefrontMode.ts` argues why it is `localStorage` and
  // never Strapi. Threaded as a prop to the two screens that act on it, exactly like `settings`
  // below; no new context. Read here, above `navItems`, because Commit B's rail filter needs it.
  const storefront = useStorefrontMode();

  // MVP-225: the Ingest screen exists only for `admin`. `role` starts null (whoami hasn't resolved,
  // or this server predates the endpoint), so the rail simply has one fewer icon until it does —
  // and a stored `view === 'ingest'` from an admin session falls back to Library for anyone else,
  // rather than rendering a screen whose every request would 403.
  // MVP-226 adds the Accounts screen behind the same `admin` gate, and MVP-227 the Activity log,
  // so the three travel together.
  // A downloader's rail is Library and nothing else. `canOperate` answers TRUE for an unresolved
  // role on purpose — see its doc comment: hiding the Cockpit on a `null` from a backend that
  // predates `whoami()` would cost an operator the main screen, which is worse than a downloader
  // seeing two icons for the ~100 ms before the role arrives. Neither gate is a security boundary;
  // the backend refuses these requests regardless.
  const t = useT();
  const ingestAllowed = canIngest(role);
  const operatorAllowed = canOperate(role);
  // MVP-464: a shop gets the Accounts rail entry without the other three — it may issue a staff
  // link, and holds none of the ingest/audit/withheld grants.
  const inviteAllowed = canInviteStaff(role);
  // MVP-508: `shop` only — a KJ holds the read half of the room routes and none of the write half,
  // so every control on that screen would be a 403 for them.
  const roomsAllowed = canManageRooms(role);
  // MVP-509: `admin` alone — the vendor's screen, above the shop's.
  const licencesAllowed = canManageLicences(role);
  // MVP-278: paying is the shop's alone, deciding a slip the admin's alone.
  const plansAllowed = canPayForPlan(role);
  const paymentsAllowed = canReviewPayments(role);
  const navItems: SidebarRailItem[] = [
    LIBRARY_NAV_ITEM,
    ...(operatorAllowed ? OPERATOR_NAV_ITEMS : []),
    ...(roomsAllowed ? [ROOMS_NAV_ITEM] : []),
    ...(licencesAllowed ? [LICENCES_NAV_ITEM] : []),
    ...(ingestAllowed ? [INGEST_NAV_ITEM] : []),
    ...(inviteAllowed ? [USERS_NAV_ITEM] : []),
    ...(ingestAllowed ? [ACTIVITY_NAV_ITEM, WITHHELD_NAV_ITEM] : []),
    ...(plansAllowed ? [PLANS_NAV_ITEM] : []),
    ...(paymentsAllowed ? [PAYMENTS_NAV_ITEM] : []),
  ]
    // MVP-500 Commit B: the rail cannot reach Settings or any admin screen while the mode is on.
    // Filtered out entirely rather than shown disabled — an icon that does nothing invites the
    // press that finds out, and the strip above already names the state.
    .filter(({ id }) => !(storefront.active && STOREFRONT_HIDDEN_VIEWS.has(id as ConsoleView)))
    .map(({ id, icon, labelKey }) => ({ id, icon, label: t(labelKey) }));
  const viewAllowed = (v: ConsoleView) =>
    (!ADMIN_VIEWS.has(v) || ingestAllowed) &&
    (!STAFF_INVITE_VIEWS.has(v) || inviteAllowed) &&
    (!ROOM_ADMIN_VIEWS.has(v) || roomsAllowed) &&
    (!LICENCE_ADMIN_VIEWS.has(v) || licencesAllowed) &&
    (!PLAN_SHOP_VIEWS.has(v) || plansAllowed) &&
    (!PAYMENT_ADMIN_VIEWS.has(v) || paymentsAllowed) &&
    (!OPERATOR_VIEWS.has(v) || operatorAllowed) &&
    // MVP-500 Commit B: same list, the other door. Toggling the mode on with Settings already up
    // must not leave the operator's config editable behind a rail that no longer offers it.
    !(storefront.active && STOREFRONT_HIDDEN_VIEWS.has(v));
  const effectiveView: ConsoleView = viewAllowed(view) ? view : 'library';
  // MVP-278: the expiry strip's ต่ออายุ opens the plans page — for the role that can pay, and not in
  // storefront mode, which hides that page (STOREFRONT_HIDDEN_VIEWS).
  // MVP-278 harden: and only on a sign-up licence — the server refuses any other (LICENCE_NOT_SELF_SERVE).
  const renewAllowed = canPayForPlan(role) && licence?.selfServe === true && !storefront.active;

  // MVP-269: the name of the view, for the `<h1>` below. It is the rail's own label rather than a
  // seventh set of page titles, because the two must never be able to disagree — a screen-reader
  // user navigating by heading and a sighted user reading the rail are being told where they are by
  // the same words. `navItems` is already role-filtered and already translated, and `effectiveView`
  // is already the fallen-back view, so a role that cannot reach a screen can never title itself
  // with one.
  const currentViewLabel = navItems.find((item) => item.id === effectiveView)?.label ?? t('nav.library');

  // MVP-335: the actual focus-move + announce, keyed on `effectiveView` alone — a locale switch on
  // the same screen is not a view change and must not re-fire this.
  useEffect(() => {
    if (!hasMountedViewRef.current) {
      hasMountedViewRef.current = true;
      return;
    }
    // MVP-490: `/` is switching to Library in order to land in its search field, so the focus move
    // would be taking focus away from the very thing the operator asked for. The ANNOUNCEMENT still
    // fires: a screen-reader user hears "Library" and then lands in a field with its own
    // `aria-label`, which is the same two facts MVP-335 promised, in the same order.
    if (suppressViewFocusRef.current) {
      suppressViewFocusRef.current = false;
    } else {
      viewRegionRef.current?.focus();
    }
    setViewAnnouncement(t('app.viewChangedAnnouncement', { view: currentViewLabel }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveView]);

  // MVP-11 #30: the status bar used to hard-code "v0.1.0". `/api/health` was the audit's
  // suggested source, but its `version` is `info.strapi` — the BACKEND FRAMEWORK version (5.52.2
  // live), not this app's. Showing that in the operator's status bar is worse than the literal it
  // replaced, so the number comes from `web/package.json` at build time instead (vite `define`),
  // which is the version of the code actually running in this tab.
  const appVersion = `v${__APP_VERSION__}`;

  // MVP-11 #16 / MVP-11 #12, rewritten by MVP-483. This used to be two pieces of local state
  // (`onReachabilityChange` plus the window's `online`/`offline` events) feeding one pinned
  // `AlertToast danger`. Both signals, and the precedence between them, now live in
  // `useConnectionHealth` — the same hook the Stage window and the guest phone read — so the three
  // surfaces cannot drift apart again. `realtimeClient` is this window's own stream; the cockpit is
  // the one surface with all three signals.
  //
  // No `onDismiss` and no disabling: offline is a state, not a dismissable failure, and controls
  // stay enabled app-wide (the queue/rotation panels already surface their own write failures —
  // DESIGN.md §6.4's "nothing silently queued" per action, not a global lockout). Nothing is queued
  // for replay, because this app has no offline write queue.
  const outage = useConnectionHealth(realtimeClient);

  const sessionInfo = useSessionInfo();
  const playback = usePlaybackStateContext();

  // MVP-11 #8: one `useOpsSettings()` call for the whole shell — SettingsScreen reads/writes
  // through this same instance rather than opening a second `GET /api/setting`.
  const settings = useOpsSettings();

  useEffect(() => {
    applyDocumentTheme(document.documentElement, settings.setting?.theme);
  }, [settings.setting?.theme]);

  // MVP-502: the palette is a second, independent axis on the same element — venue-wide and
  // server-persisted like the theme above, so a room gets one answer rather than per-device drift.
  // Its own effect rather than a second line in the theme one: the two values change separately and
  // there is no reason for a theme write to re-run the palette write.
  useEffect(() => {
    applyDocumentPalette(document.documentElement, settings.setting?.palette);
  }, [settings.setting?.palette]);

  // MVP-490: the console-wide `/`. One `document` keydown, live only while Library is NOT the
  // visible view — LibraryScreen's own listener (gated on `active`) owns the key there, so the two
  // never both fire. `consoleSearchFocus.ts` holds the decision and the cross-Suspense handoff;
  // this is the wiring. It reads the effective binding table, so remapping `search` in
  // Settings > Shortcuts moves this key on these six views. It does NOT move it on Library:
  // `libraryActions.ts` hard-codes `/` there, so a remap to `f` gives `f` on the six and leaves `/`
  // as Library's own. Out of scope here — noted so the contract is not read as stronger than it is.
  useEffect(() => {
    const bindings = effectiveHotkeyBindings(settings.ops.hotkeys);
    function onKeyDown(e: KeyboardEvent) {
      const open = shouldOpenConsoleSearch(
        e,
        {
          view: effectiveView,
          isTypingTarget: isTypingTarget(e.target as { tagName?: string; isContentEditable?: boolean } | null),
          // Live DOM, not component state — same reason LibraryScreen checks it this way: it is
          // then correct for a dialog this shell does not itself track, and never goes stale.
          isDialogOpen: document.querySelector('[role="dialog"]') != null,
          isCapturingHotkey: document.querySelector('[data-hotkey-capture]') != null,
          // AdminIngestScreen is conditionally rendered and holds the picked File objects plus a
          // live AbortController in local state — leaving it mid-run throws both away.
          isIngestRunning: document.querySelector('[data-ingest-running]') != null,
        },
        bindings
      );
      if (!open) return;
      // Firefox opens its quick-find on `/` otherwise, eating the keystroke we just claimed.
      e.preventDefault();
      requestSearchFocus();
      suppressViewFocusRef.current = true;
      setView('library');
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [effectiveView, settings.ops.hotkeys, setView]);

  // P2 fix (round 5): StrictMode (main.tsx) double-invokes this effect on mount with no cleanup
  // to cancel the request itself — `GET /api/singers` was confirmed live firing twice per load.
  // Same "already ran" ref guard as useRotation.ts/useSessionInfo.ts's matching fix.
  const singersFetchedRef = useRef(false);
  useEffect(() => {
    if (singersFetchedRef.current) return;
    singersFetchedRef.current = true;
    let cancelled = false;
    listSingers()
      .then((rows) => {
        if (!cancelled) setSingers(rows);
      })
      .catch(() => {
        /* best-effort — SingerPicker still works typing a brand-new name with an empty reuse list */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // BE-13 Wave 2 Builder B: singer resolution now goes through the rotation engine's own
  // `addSinger` (creates-or-reuses the `Singer` row AND a rotation slot in one call, SPEC §4.1.1)
  // instead of `api/singers.ts`'s plain-CRUD `findOrCreateSinger` — a name with no slot in
  // tonight's rotation yet needs a slot created regardless of whether the `Singer` row itself is
  // brand new. A 409 `SINGER_NAME_TAKEN` means this exact name already holds a live slot tonight
  // (added earlier from Library or the cockpit) — not an error from this caller's point of view,
  // just "reuse the existing one", so this re-reads the snapshot and matches by name instead of
  // failing the whole enqueue.
  const resolveSinger = useCallback(
    async (pick: { singerId?: string; name: string }): Promise<{ documentId: string; name: string; defaultKeyShift?: number | null }> => {
      const known = pick.singerId ? singers.find((s) => s.documentId === pick.singerId) : undefined;
      const name = (known?.name ?? pick.name).trim();
      try {
        const { snapshot, meta } = await addSingerToRotation({ name, mode: 'bottom' });
        const slot = snapshot.slots.find((s) => s.documentId === meta.slotId);
        if (!slot?.singer) throw new Error(`"${name}" was added, but the rotation slot came back without a singer.`);
        const singer = slot.singer;
        setSingers((prev) =>
          prev.some((s) => s.documentId === singer.documentId)
            ? prev
            : [{ id: 0, documentId: singer.documentId, name: singer.name, isRegular: singer.isRegular, defaultKeyShift: singer.defaultKeyShift, colorTag: singer.colorTag }, ...prev]
        );
        return singer;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'SINGER_NAME_TAKEN') {
          const { snapshot } = await getRotationState();
          const target = name.toLowerCase();
          const existingSlot = snapshot.slots.find((s) => s.singer?.name.trim().toLowerCase() === target);
          if (existingSlot?.singer) return existingSlot.singer;
        }
        throw err;
      }
    },
    [singers]
  );

  const handleQueue = useCallback(
    async (song: Song, pick: { singerId?: string; name: string }, mode: 'next' | 'bottom') => {
      const session = sessionInfo.session;
      if (!session) throw new Error('No session running — start one from the Cockpit tab first.');
      const singer = await resolveSinger(pick);
      // Rank is entirely server-side now (`computeQueueInsertRank`, SPEC §4.1.1) — 'next'/'bottom'
      // map straight onto the rotation engine's own insert modes, no client-computed rank left.
      await createRotationQueueEntry({
        singerId: singer.documentId,
        songId: song.documentId,
        keyShift: singer.defaultKeyShift ?? 0,
        mode: mode === 'next' ? 'next' : 'bottom',
      });
    },
    [sessionInfo.session, resolveSinger]
  );

  const handlePlayNow = useCallback(
    (song: Song) => {
      setQueueError(null);
      // P2 fix (round 3): an ad-hoc "Play now" from the Library bypasses the rotation entirely —
      // this patches playback state directly and never touches a queue entry (there usually isn't
      // one for `song` at all). But the ROTATION LIST's own "Playing" badge (QueueRow.tsx) is
      // derived from `entry.status === 'playing'`, which is a SEPARATE, unrelated row — confirmed
      // live: playing an ad-hoc song left the previous queue entry showing "Playing" underneath the
      // new now-playing header. Snapshot it before the patch (once playback state flips, `queueEntry`
      // points at the NEW song, i.e. null) and mark it `done` so the two views agree again.
      const previousEntry = playback.state?.queueEntry ?? null;
      playback
        .patch({
          song: song.documentId,
          queueEntry: null,
          // P2 fix (round 5): see `cuedSongPatch`'s own doc comment — this no longer claims the
          // song is already playing before anything has actually started decoding.
          //
          // R11-P2-4 fix (round 11, live-Chrome critic): this used to stamp its own raw
          // `positionAtServerMs: Date.now()` here — a LOCAL-clock timestamp bypassing
          // `usePlaybackState.ts`'s `patch()` -> `withPositionStamp`/`serverNowMs` pipeline every
          // other call site in the app funnels through (see `serverNowMs`'s own doc comment for
          // the cross-device clock bias that skews). Omitting the field entirely lets `patch()`
          // stamp it itself, in the server's clock domain, exactly like every other write that
          // touches `positionMs`/`isPlaying`.
          ...cuedSongPatch(0),
        })
        .then(async (updated) => {
          // P0 fix (round 6): `patch()` no longer rejects on failure (usePlaybackState.ts's own
          // doc comment) — it resolves `null` instead, so a failed write (e.g. rate-limited) must
          // be checked explicitly here rather than relying on a `.catch()` that will never fire.
          if (!updated) {
            setQueueError(playback.patchError?.message ?? t('app.playbackStartFailed'));
            return;
          }
          if (previousEntry && previousEntry.status === 'playing') {
            // Best-effort: a stale/already-advanced entry (e.g. a race with "Start next") 404s
            // harmlessly here — the rotation list is still correct either way.
            await patchRotationQueueEntry(previousEntry.documentId, { status: 'done' }).catch(() => {});
          }
          // R8-P0-1 fix (round 8, live-Chrome critic): this patch alone never started playback —
          // `cuedSongPatch` deliberately leaves `isPlaying:false` (see its own doc comment) until
          // something actually calls `togglePlay()`. The rotation's own "Play now"/"Start next"
          // work because `OperatorConsole.tsx`'s `advance()` sets this SAME singleton, watched by
          // an effect that fires `togglePlay()` once the cued song is the one actually loaded —
          // `pendingAutoplayRef.ts`'s own doc comment covers why a module singleton, not a prop,
          // is what lets this ad-hoc, queue-free path reach that same mechanism from here.
          pendingAutoplaySongIdRef.current = song.documentId;
          setView('cockpit');
        })
        .catch((err: unknown) => setQueueError(apiErrorMessage(err, t('app.playbackStartFailed'), t)));
    },
    [playback]
  );

  return (
    <div className="app-shell">
      {/* MVP-11 #30: DESIGN.md §6.1 — "a Skip to content link is the first focusable element on
          every page". New class, H's to style (ownership matrix): `.skip-link`. */}
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>
      <AppHeader>
        {/* MVP-231: the email was a `<span>` for as long as there was nothing behind it. It is the
            place a person already looks to see who they are signed in as, so it is also where their
            own account lives. */}
        {user && (
          <Button variant="ghost" size="sm" className="app-header__user" onClick={() => setProfileOpen(true)}>
            {/* The display name is what the person chose to be called; the email is the login
                identifier. Show the name when there is one, and only fall back to the identifier
                when there is not. `username` sits between them because every account has one. */}
            {user.displayName || user.username || user.email}
          </Button>
        )}
        {/* MVP-500: the way in. Hidden once the mode is on, because the way OUT lives in the strip
            below instead — see `StorefrontModeBar.tsx` for why the two controls sit in different
            places. This mode has no keyboard accelerator: an accelerator must never be the only way
            into a state that takes keys away. */}
        {!storefront.active && (
          <Button variant="ghost" size="sm" className="app-header__storefront" onClick={() => storefront.setActive(true)}>
            {t('ops.storefront.enter')}
          </Button>
        )}
        {/* MVP-687: the manuals are static pages under public/manual/ (built by
            docs/manual/build.mjs). An anchor styled as a button, not a Button, because it navigates.
            ponytail: opens the list of all three rather than guessing a book from the role — a KJ
            and an owner both pick theirs in one click, and the list never picks wrong. */}
        <a className="btn btn--ghost btn--sm" href="/manual/index.html" target="_blank" rel="noopener">
          <span className="text-body">{t('app.manual')}</span>
        </a>
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          {t('app.signOut')}
        </Button>
      </AppHeader>

      {user && <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} user={user} role={role} />}

      {/* MVP-483: the app-wide outage strip, replacing MVP-11 #16's floating `AlertToast danger`
          for this ONE state. A centred pill is right for a failure that belongs to a control the
          operator just used (MVP-462's owner decision, quoted verbatim in `ui/AlertToast.tsx`, and
          still what every other alert on this screen uses) — but this is not that. It is the whole
          window being out of date, it cannot be dismissed, and it has to be true of every screen
          the rail can switch to, so it is a strip across the top rather than something floating
          over one of them. Rendered ONLY for app scope: an SSE drop with REST alive is region
          scope and says so beside the rotation instead (`OperatorConsole.tsx`).
          `role="status"`, not `alert`: nothing here interrupted the operator. */}
      {outage.scope === 'app' && (
        <div className="app-outage-strip" role="status" data-testid="app-outage-strip">
          {/* MVP-272: `useConnectionHealth`/`connectionLabel` hand back keys — the separators
              joining them are layout, not copy, so they stay here rather than being baked into any
              of the three sentences. `host` is null when `navigator.onLine` is false: the browser
              never dialled a host, so there is none to name (see `deriveOutage`). */}
          <strong className="app-outage-strip__title">{t(outage.titleKey)}</strong>
          {outage.host && <span>{t('common.connection.hostUnreachable', { host: outage.host })}</span>}
          <span>{t('common.outage.stale')}</span>
          {outage.descriptionKey && <span>{t(outage.descriptionKey)}</span>}
        </div>
      )}

      {/* MVP-500: a sibling of the outage strip above, and in that slot on purpose — App.css's one
          chrome-hiding rule deliberately excludes `.app-outage-strip`, and a test beside that rule
          pins the exclusion, so this row stays visible under the same conditions the stale-data
          warning does: including while the browser is showing only the document. An unattended room
          PC is exactly where nobody is standing to notice otherwise. It renders nothing at all
          while the mode is off. */}
      <StorefrontModeBar active={storefront.active} onExit={() => storefront.setActive(false)} />

      {/* MVP-513: the licence countdown, in the same slot and for the same reason — the row of the
          shell that `App.css`'s chrome-hiding rule spares, so an unattended room PC showing only the
          document still shows it. It renders nothing at all outside the fortnight either side of
          `expiresAt`, nothing for an admin, and nothing once dismissed for today; all three of those
          decisions live in `LicenceExpiryBar.tsx`/`licenceCountdown.ts` rather than here. */}
      <LicenceExpiryBar licence={licence} role={role} onRenew={renewAllowed ? () => setView('plans') : undefined} />

      <div className="app-shell__body">
        <SidebarRail
          items={navItems}
          activeId={effectiveView}
          onSelect={(id) => setView(id as ConsoleView)}
          navLabel={t('common.mainNavigation')}
        />

        {/* MVP-269: a `<main>`, not a div. The skip link above has pointed at `#main` since MVP-11
            and worked as a scroll target, but it landed on nothing semantic — measured live in
            Chrome on 2026-09-08, all six admin views reported `main: 0` and `h1: 0`, with
            `header`/`nav`/`aside`/`footer` present and the content itself unlabelled. So a
            screen-reader user had no landmark to jump the rail into, and headings started at `h2`
            under no `h1` at all. One element change fixes the skip link's destination and the
            missing landmark together. */}
        <main className="app-shell__content" id="main">
          {/* Visually hidden because every one of these screens is a working surface with no room
              for a page title, and inventing one would cost the KJ vertical space to tell them
              something the rail already shows them. The `h2` section headings each view already
              renders now sit under an `h1` instead of floating at the top of an empty hierarchy.
              The Activity view's first `h2` repeats this text; that redundancy is the price of one
              rule covering six screens, and it reads as a heading and its section rather than as a
              mistake. */}
          <h1 className="visually-hidden">{currentViewLabel}</h1>
          {/* MVP-335: the actual screen-reader announcement for a rail switch. `role="status"` +
              `aria-live="polite"` on a persistent node whose TEXT changes — the earlier h1 is a
              heading, read once if focus (or a virtual cursor) ever lands on it, but nothing moves
              focus there, and nothing before this task told an already-listening screen reader that
              anything had happened at all. */}
          <p className="visually-hidden" role="status" aria-live="polite">
            {viewAnnouncement}
          </p>
          {/* MVP-335: the focus target for the same view change. `tabIndex={-1}` keeps it out of the
              normal Tab order (like the skip link's own target) — it is only ever reached
              programmatically, from the effect above, immediately after `effectiveView` changes. One
              wrapper around all six screens (rather than one hook per screen) is what let this land
              without opening LibraryScreen.tsx/AdminUsersScreen.tsx/SettingsScreen.tsx, which other
              work in flight owns concurrently. */}
          <div ref={viewRegionRef} tabIndex={-1} className="app-shell__view">
          {/* R8-P1-4/NEW-P2-1 fix (round 8, live-Chrome critic): OperatorConsole (which owns the
              only `usePlaybackEngine`/`<video>` pair in the app) used to be conditionally rendered
              here, unmounted the instant the KJ switched to Library — confirmed live: playback
              died mid-song, and a reload landing on Library had no `<video>` at all for anything to
              resume into. It now stays mounted for the lifetime of `ConsoleShell` regardless of
              `view`, hidden (`display:none`, not unmounted — a hidden `<video>` keeps decoding
              audio/video exactly as before) rather than swapped out, so the engine and its
              rotation/prefetch/gapless state survive a tab switch.

              Library is kept mounted the same way as of a later fix: reported live — leaving
              Cockpit for Library used to reset the catalog's scroll position and search/filter
              state and re-fetch from scratch every time, because a conditionally-rendered Library
              is a brand-new component instance on every switch back, with brand-new (empty)
              `useState`. It holds no persistent PLAYBACK state the way Cockpit does, but its local
              search/filter state and the virtualized grid/list's scroll offset are exactly the
              kind of state a KJ expects to find undisturbed after a quick trip to Cockpit and back,
              so the same "hide, don't unmount" treatment applies. `active` tells it which of the
              two it currently is, so its own document-level keydown listener (Enter/Q/arrows/etc.)
              can detach while hidden instead of firing invisibly on top of Cockpit's — the same
              class of bug `useHotkeys`'s `active` gating below exists to prevent for OperatorConsole.

              MVP-250: the wrapper is `.app-shell__screen` now. It was originally styled inline
              ONLY to avoid touching `App.css` while another task held that file, and the missing
              `flex:1; min-height:0` broke the Cockpit's grid — see that rule's comment in
              App.css. Only the `display:none` stays inline, which is what actually hides it. */}
          <div className="app-shell__screen" style={effectiveView === 'library' ? undefined : { display: 'none' }}>
            {queueError && (
              <AlertToast variant="danger" onDismiss={() => setQueueError(null)} dismissLabel={t('common.dismiss')}>
                {queueError}
              </AlertToast>
            )}
            {/* MVP-365: the boundary is INSIDE the always-mounted wrapper, so the fallback shows
                in the Library's own pane rather than replacing the shell around it. */}
            <Suspense fallback={<LoadingPane />}>
            <LibraryScreen
              singers={singers}
              onQueue={handleQueue}
              onPlayNow={handlePlayNow}
              settings={settings}
              // MVP-224: `canDownload(role)` is the one place `role.type` is compared — this
              // component never sees a role-name string, just the resolved boolean.
              canDownload={canDownload(role)}
              // MVP-459: same contract again — downloader only, resolved here beside the others.
              canAudition={canAudition(role)}
              canOperate={operatorAllowed}
              // MVP-354: same contract as `canDownload` above — admin only, resolved here so the
              // screen never holds a role-name string of its own.
              canDeleteSongs={canDeleteSongs(role)}
              active={effectiveView === 'library'}
              // MVP-500: wired now, consumed by the key lock in Commit B — the same prop-drill
              // `settings` above uses, so neither screen needs a context of its own.
              storefrontActive={storefront.active}
            />
            </Suspense>
          </div>
          {/* One boundary for all five: they are mutually exclusive by `effectiveView`, so only one
              can ever be suspending, and five identical `<Suspense>` wrappers would say nothing the
              condition above does not already say. */}
          <Suspense fallback={<LoadingPane />}>
            {effectiveView === 'ingest' && <AdminIngestScreen />}
            {effectiveView === 'users' && (
              <AdminUsersScreen
                currentUserId={user?.id}
                role={role}
                manageAccounts={canManageAccounts(role)}
                manageStaff={canManageStaff(role)}
              />
            )}
            {effectiveView === 'activity' && <AdminActivityScreen />}
            {effectiveView === 'withheld' && <AdminWithheldScreen />}
            {/* MVP-599: `canMoveSessionRoom` is its own gate and NOT `canManageRooms` — the grant
                sits on `AUTHENTICATED_ACTIONS`, so a KJ may walk their own night into another room
                without holding the back-office right to decide what rooms exist. Resolved here with
                the rest, the `canDeleteSongs` contract.
                MVP-642: `canRotateJoinCode` mirrors a SEPARATE grant that happens to sit on the same
                list two lines above `moveRoom`. Passed as its own prop rather than reusing the one
                beside it, so the day the owner moves either grant, only the control it belongs to
                changes. */}
            {effectiveView === 'settings' && (
              <SettingsScreen
                settings={settings}
                sessionInfo={sessionInfo}
                canMoveRoom={canMoveSessionRoom(role)}
                canRotateCode={canRotateJoinCode(role)}
                canEditPayTo={canReviewPayments(role)}
              />
            )}
            {/* MVP-508: `licence` is already resolved here for the countdown bar, and it is where
                the room cap will arrive when `whoami` starts sending it. */}
            {effectiveView === 'rooms' && <RoomsScreen licence={licence} />}
            {/* MVP-509: no props. This screen reads the licence ROSTER off `/api/licences`, which is
                a different thing from the single licence `whoami` attaches to an operator account —
                a vendor login has none of its own. */}
            {effectiveView === 'licences' && <LicencesScreen />}
            {effectiveView === 'plans' && <PlansScreen licence={licence} />}
            {effectiveView === 'payments' && <AdminPaymentsScreen />}
          </Suspense>
          {/* `operatorAllowed` is the one condition that may unmount this, and it is not a tab
              switch: a downloader can never reach the Cockpit, so keeping the decoder alive for
              them buys nothing and costs a stream of 403s from the console's own polling. For
              everyone else the mounted-for-the-session behaviour above is unchanged. */}
          {operatorAllowed && (
            <div className="app-shell__screen" style={effectiveView === 'cockpit' ? undefined : { display: 'none' }}>
              <Suspense fallback={<LoadingPane />}>
                <OperatorConsole
                  joinPath={guestPath}
                  active={effectiveView === 'cockpit'}
                  settings={settings}
                  storefrontActive={storefront.active}
                  canTagGuideVocal={canTagGuideVocal(role)}
                />
              </Suspense>
            </div>
          )}
          </div>
        </main>
      </div>

      <StatusBar
        left={<ConnectionIndicator />}
        center={<MiniTransportBar view={effectiveView} />}
        right={<span className="tabular-nums">{appVersion}</span>}
      />
    </div>
  );
}

function LoadingPane() {
  return (
    <div className="app-loading">
      <Skeleton variant="row" />
      <Skeleton variant="row" />
      <Skeleton variant="row" />
    </div>
  );
}

// P2 fix (round 3): on a fresh console load this used to start alongside LibraryScreen's search,
// useSessionInfo's fetch and listSingers() — all in the same tick. Confirmed live: the very first
// `/api/catalog/search` AND `/api/realtime/stream` of a load each came back 503 before an
// immediate identical retry succeeded (not reproducible via curl, so this is about concurrency at
// connect time, not the routes themselves — the actual fix, if any, is server/proxy-side and out
// of this wave's reach). ponytail: a flat delay is not a real fix, only a cheap way to stop the
// SSE connection from being one more request in that exact opening burst.
const REALTIME_START_DELAY_MS = 250;

export function App() {
  const route = useRoute();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [user, setUser] = useState<StrapiUser | null>(null);
  // MVP-224: `null` until `whoami()` resolves (or forever, if it never does — see that function's
  // own doc comment on why every failure mode degrades to `null` rather than throwing here).
  const [role, setRole] = useState<UserRole | null>(null);
  // MVP-513: the other half of the same `whoami()` answer. `null` means "nothing to say" — no
  // licence on this account, or a backend that predates the block — never "no expiry"; the strip
  // below simply does not render for it.
  const [licence, setLicence] = useState<LicenceSnapshot | null>(null);
  // MVP-318: a dictionary key, not a finished sentence — see `LoginDialog.tsx`'s own `reason` prop
  // doc comment for why (this used to be a raw English string built inline below, rendered verbatim
  // regardless of locale).
  const [reauthReason, setReauthReason] = useState<MessageKey | undefined>(undefined);

  const handleLoggedIn = useCallback((loggedInUser: StrapiUser) => {
    setUser(loggedInUser);
    setAuthed(true);
    setReauthReason(undefined);
  }, []);

  const isConsole = route.kind === 'console';
  // MVP-224: declared up here (rather than alongside its own effect further down, next to
  // `meFetchedRef`) because the `onUnauthorized` handler just below needs to reset it.
  const roleFetchedRef = useRef(false);

  // The one shared, authenticated realtime connection (DESIGN.md §6.4) is a console-only concern.
  // The Stage window reads live data through its own paths instead (BroadcastChannel for the
  // common same-machine-second-monitor case, plus each read hook's own poll — usePlaybackState.ts/
  // useRotation.ts/useSessionInfo.ts's own doc comments) and the guest page opens its own separate,
  // unauthenticated `RealtimeClient` inside RequestPage.tsx — starting this shared, JWT-bearing
  // client on either of those routes would be pointless at best (Stage) and a token leak at worst
  // (a KJ who opens a guest link in the same browser as the console must never have this client
  // touch that tab).
  useEffect(() => {
    if (!isConsole || !authed) {
      realtimeClient.stop();
      return;
    }
    const timer = setTimeout(() => realtimeClient.start(), REALTIME_START_DELAY_MS);
    return () => {
      clearTimeout(timer);
      realtimeClient.stop();
    };
  }, [isConsole, authed]);

  // A 401 from any API call (expired/invalid JWT) drops straight back to sign-in (DESIGN.md
  // §6.4 "reauth" — this app has no refresh-token endpoint to retry silently, so the honest
  // move is asking the operator to sign in again rather than pretending to recover).
  useEffect(() => {
    return onUnauthorized(() => {
      setAuthed(false);
      setUser(null);
      // MVP-224: drop the stale role along with `user` and let the next sign-in re-fetch it —
      // unlike `meFetchedRef` below, this ref IS reset here, since there's no `handleLoggedIn`
      // equivalent that hands this screen a fresh role straight from the login response.
      setRole(null);
      // MVP-513: the licence travels with the role, and a countdown left standing over a sign-in
      // dialog would be a sentence about a session that no longer exists.
      setLicence(null);
      roleFetchedRef.current = false;
      setReauthReason('auth.sessionExpired');
    });
  }, []);

  // P2 fix: a reload keeps the JWT (`authed` starts `true` from `isAuthenticated()`) but not the
  // in-memory `user` set by `handleLoggedIn` — without this, the header showed only "Sign out"
  // until the next fresh sign-in. Console-only (`isConsole`): `user` is never rendered outside
  // `ConsoleShell`'s header, so Stage/guest have no reason to spend a request on it. A failure
  // here (expired token) already flows through the `onUnauthorized` listener above.
  //
  // P2 fix (round 5): StrictMode double-invoked this effect on mount, and `user` staying `null`
  // across both invocations (neither `me()` call has resolved yet) meant the guard above did not
  // stop a second, identical `GET /api/users/me` — confirmed live. `authed`/`user` genuinely going
  // false-then-true again only ever happens via `onSignOut`/`onUnauthorized`, both of which set
  // `user` back to `null` too, and a subsequent sign-in sets `user` directly (`handleLoggedIn`)
  // rather than relying on this effect — so, in practice, this fetch is a true one-shot per mount,
  // making a plain "already ran" ref (same idiom as useRotation.ts/useSessionInfo.ts) safe here.
  const meFetchedRef = useRef(false);
  useEffect(() => {
    if (!isConsole || !authed || user || meFetchedRef.current) return;
    meFetchedRef.current = true;
    // MVP-226: same StrictMode/ref-guard interaction as the `whoami()` effect below — see its
    // comment. Confirmed live in the same session: after a reload the header showed no email and
    // `AdminUsersScreen` could not tell which row was the signed-in admin's own (the server still
    // refused a self-lockout with SELF_LOCKOUT, but the UI stopped greying the controls out).
    me()
      .then(setUser)
      .catch(() => {});
  }, [isConsole, authed, user]);

  // MVP-224: sibling fetch to the `me()` one above, same one-shot-per-session shape — but gated
  // only on `roleFetchedRef`, not on `role` being falsy, because `null` is whoami()'s normal
  // "no answer yet / not deployed" resting state (see that function's own doc comment), not a
  // "still needs fetching" signal the way `!user` is for `me()`. `whoami()` itself never rejects,
  // so there is no `.catch()` here to swallow into an error toast, a login loop, or a retry storm —
  // exactly the degrade-gracefully contract this task calls for. (`roleFetchedRef` itself is
  // declared earlier, alongside `isConsole` — the `onUnauthorized` effect above needs to reset it.)
  //
  // MVP-226: the `cancelled` flag this used to carry made the ref guard and StrictMode cancel each
  // other out. On a MOUNT with `authed` already true (any page reload with a stored JWT), React
  // runs the effect, runs its cleanup, then runs it again: the cleanup set `cancelled = true` for
  // the only fetch in flight, and the second run hit `roleFetchedRef.current` and started no
  // replacement — so `role` stayed null and the two admin-only rail entries (Ingest, and now
  // Accounts) never appeared until the next fresh sign-in. Confirmed live in Chrome: present right
  // after login, gone after F5, with `GET /api/session/whoami` returning 200 the whole time.
  // Production never showed it (StrictMode only double-invokes in dev), which is exactly why it
  // survived MVP-225 unnoticed. There is nothing to cancel here: `whoami()` never rejects, and a
  // `setRole` after unmount is a no-op in React 18+.
  useEffect(() => {
    if (!isConsole || !authed || roleFetchedRef.current) return;
    roleFetchedRef.current = true;
    // MVP-513: one request, two facts. `session` is null on every failure mode `whoami()` swallows,
    // and both pieces of state stay at their own "nothing known" resting value for it.
    whoami().then((session) => {
      setRole(session?.role ?? null);
      setLicence(session?.licence ?? null);
    });
  }, [isConsole, authed]);

  // Stage and guest are mounted BEFORE any auth branch (StageRoot.tsx / RequestPage.tsx's own doc
  // comments both require this) — neither may ever see LoginDialog, AppHeader or
  // ConnectionIndicator, regardless of whether this same browser also has an operator JWT on file.
  if (route.kind === 'stage') {
    return (
      <Suspense fallback={<LoadingPane />}>
        <StageRoot />
      </Suspense>
    );
  }

  if (route.kind === 'guest') {
    return <RequestPage joinCode={route.joinCode} />;
  }

  // MVP-226: `/invite/<token>`, mounted before the auth branch for the same reason Stage and guest
  // are — the person following this link has no account yet, and an operator's own JWT happening to
  // sit in this browser must not turn their invite into the console.
  if (route.kind === 'invite') {
    return <InviteRedeemScreen token={route.token} />;
  }

  // MVP-278: `/signup`, before the auth branch for the invite's reason — a stranger has no account,
  // and a JWT already in this browser must not turn the sign-up page into the console.
  if (route.kind === 'signup') {
    return <SignupScreen />;
  }
  if (route.kind === 'signupConfirm') {
    return <SignupConfirmScreen token={route.token} />;
  }

  if (!authed) {
    return <LoginDialog onSuccess={handleLoggedIn} reason={reauthReason} />;
  }

  return (
    <ConsoleShell
      user={user}
      role={role}
      licence={licence}
      onSignOut={() => {
        logout();
        setAuthed(false);
        setUser(null);
        // MVP-513: same reasoning as the role below — and the next account signing in at this
        // machine must never inherit the previous one's contract.
        setLicence(null);
        // MVP-224: same reasoning as the `onUnauthorized` listener above — no `handleLoggedIn`-style
        // shortcut hands this screen a fresh role, so the ref must be cleared for the next sign-in
        // to actually re-fetch rather than silently keep whatever role the previous session had.
        setRole(null);
        roleFetchedRef.current = false;
      }}
    />
  );
}
